import type {TimestampField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {useMemo} from 'react'

type EventStatus = {
  variant: 'live' | 'primary'
  label: 'LIVE' | 'PAST EVENT' | 'TODAY' | 'TOMORROW' | 'UPCOMING'
}

const createStatus = (label: EventStatus['label'], variant: EventStatus['variant'] = 'primary'): EventStatus => ({
  variant,
  label,
})

const toMidnight = (date: Date): Date => {
  const normalized = new Date(date)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

const getDaysDifference = (from: Date, to: Date): number => {
  const fromMidnight = toMidnight(from)
  const toMidnightDate = toMidnight(to)
  const diffMs = toMidnightDate.getTime() - fromMidnight.getTime()
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

type EventDateData = {
  eventDate: TimestampField | undefined
  eventFinishDate: TimestampField | undefined
  eventType: string | null | undefined
}

export const getEventStatus = (data: EventDateData): EventStatus | null => {
  if (!isFilled.timestamp(data.eventDate)) {
    return null
  }

  const now = new Date()
  const eventDate = new Date(data.eventDate)
  const eventFinishDate = isFilled.timestamp(data.eventFinishDate) ? new Date(data.eventFinishDate) : null

  if (eventFinishDate && now > eventFinishDate) {
    return createStatus('PAST EVENT')
  }

  if (data.eventType === 'Online') {
    if (eventFinishDate) {
      if (now >= eventDate && now <= eventFinishDate) {
        return createStatus('LIVE', 'live')
      }
    } else {
      const diffDays = getDaysDifference(now, eventDate)

      if (diffDays === 0 && now >= eventDate) {
        return createStatus('LIVE', 'live')
      }
    }
  }

  const diffDays = getDaysDifference(now, eventDate)

  if (diffDays < 0) {
    return createStatus('PAST EVENT')
  }

  if (diffDays === 0) {
    return createStatus('TODAY')
  }

  if (diffDays === 1) {
    return createStatus('TOMORROW')
  }

  return createStatus('UPCOMING')
}

export const useGetEventStatus = (data: EventDateData) => {
  const eventStatus = useMemo(() => getEventStatus(data), [data])

  return eventStatus
}
